import React, { useState, useEffect } from "react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { register, login, verifierCode2FA, renvoyerCode2FA } from "../services/LoginService";
import CodeVerification from "../components/CodeVerification";
import { useDispatch } from "react-redux";
import { setAuth } from "../Redux/authSlice";
import { useNavigate, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
    Loader2,
    Eye,
    EyeOff,
    Megaphone,
    Users,
    Headset,
    Rocket,
} from "lucide-react";

// ══════════════════════════════════════════════════════════
//  VALIDATION
// ══════════════════════════════════════════════════════════

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
// Numéros français : 0X XX XX XX XX, avec ou sans espaces/points, ou +33
const TEL_REGEX = /^(?:(?:\+33|0)\s?[1-9](?:[\s.-]?\d{2}){4})$/;
const SIRET_REGEX = /^\d{14}$/;

function champErreur(champ, valeur) {
    switch (champ) {
        case "nomComplet":
            return valeur.trim().length < 2 ? "Le nom complet est requis." : "";
        case "email":
            return !EMAIL_REGEX.test(valeur.trim()) ? "Adresse email invalide." : "";
        case "password":
            return valeur.length < 6 ? "6 caractères minimum." : "";
        case "telephone":
            return !TEL_REGEX.test(valeur.replace(/\s/g, "")) ? "Numéro de téléphone invalide." : "";
        case "siret":
            return valeur && !SIRET_REGEX.test(valeur.replace(/\s/g, ""))
                ? "Le SIRET doit contenir 14 chiffres."
                : "";
        default:
            return "";
    }
}

// ══════════════════════════════════════════════════════════
//  PETITS COMPOSANTS
// ══════════════════════════════════════════════════════════

const Champ = ({ label, required, error, children }) => (
    <div className="w-full flex flex-col gap-1.5">
        <label className="text-white text-sm font-medium">
            {label} {required && <span className="text-red-400">*</span>}
        </label>
        {children}
        {error && <p className="text-red-300 text-xs mt-0.5">{error}</p>}
    </div>
);

const inputClasses = (hasError) =>
    `w-full px-4 py-3 text-black text-sm rounded-lg border transition
     focus:outline-none focus:ring-2 focus:ring-color6/50
     ${hasError ? "border-red-400" : "border-gray-300"}`;

const BoutonSubmit = ({ loading, children }) => (
    <button
        type="submit"
        disabled={loading}
        className={`flex items-center justify-center gap-2 bg-color6 text-white font-bold
                    rounded-xl w-auto mx-auto px-8 py-3 mt-2 transition
                    ${loading ? "opacity-70 cursor-not-allowed" : "hover:opacity-90"}`}
    >
        {loading && <Loader2 size={18} className="animate-spin" />}
        {children}
    </button>
);

// ══════════════════════════════════════════════════════════
//  PAGE
// ══════════════════════════════════════════════════════════

export default function DevenirPartenairePage() {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const [mode, setMode] = useState("login"); // "register" ou "login"
    const [loading, setLoading] = useState(false);

    // Inscription
    const [nomComplet, setNomComplet] = useState("");
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [telephone, setTelephone] = useState("");
    const [entreprise, setEntreprise] = useState("");
    const [siret, setSiret] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [erreursInscription, setErreursInscription] = useState({});

    // Login
    const [loginEmail, setLoginEmail] = useState("");
    const [loginPassword, setLoginPassword] = useState("");
    const [showLoginPassword, setShowLoginPassword] = useState(false);

    // 🔐 Double authentification (code reçu par mail)
    const [deuxFacteurs, setDeuxFacteurs] = useState(null); // { challengeId, email } ou null

    const avantages = [
        { icone: Megaphone, texte: "Sponsorisez vos événements pour plus de visibilité" },
        { icone: Users, texte: "Collaborez avec des artistes et organisateurs locaux" },
        { icone: Headset, texte: "Recevez du support personnalisé pour vos projets" },
        { icone: Rocket, texte: "Boostez la portée de vos créations facilement" },
    ];

    // Vérification token
    useEffect(() => {
        const token = localStorage.getItem("token") || sessionStorage.getItem("token");
        if (token) {
            navigate("/EspaceClient");
        }
    }, [navigate]);

    // ── Validation live d'un champ d'inscription ──
    const validerChamp = (champ, valeur) => {
        setErreursInscription((prev) => ({ ...prev, [champ]: champErreur(champ, valeur) }));
    };

    // Inscription
    const handleRegister = async (e) => {
        e.preventDefault();

        const erreurs = {
            nomComplet: champErreur("nomComplet", nomComplet),
            email: champErreur("email", email),
            password: champErreur("password", password),
            telephone: champErreur("telephone", telephone),
            siret: champErreur("siret", siret),
        };
        setErreursInscription(erreurs);

        if (Object.values(erreurs).some(Boolean)) {
            toast.error("Merci de corriger les champs en rouge.");
            return;
        }

        setLoading(true);

        try {
            const userData = {
                nomComplet,
                email,
                password,
                telephone,
                siret,
                raison_sociale: entreprise || null,
            };

            const response = await register(userData);

            if (response.success) {
                toast.success(response.message || "Inscription réussie !");
                dispatch(setAuth({ token: response.token, user: response.utilisateur }));
                localStorage.setItem("token", response.token);
                navigate("/EspaceClient");
            } else {
                toast.error(response.message || "Erreur lors de l'inscription.");
            }
        } catch (err) {
            console.error(err);
            toast.error("Erreur inattendue lors de l'inscription.");
        } finally {
            setLoading(false);
        }
    };

    // Connexion
    const handleLogin = async (e) => {
        e.preventDefault();
        if (!loginEmail || !loginPassword) {
            toast.error("Veuillez remplir tous les champs !");
            return;
        }

        setLoading(true);
        try {
            const response = await login({ email: loginEmail, password: loginPassword });

            // 🔐 Code envoyé par mail → on affiche l'écran de saisie du code
            if (response.success && response.twoFactorRequired) {
                setDeuxFacteurs({ challengeId: response.challengeId, email: response.email });
                toast.info(response.message || "Un code vous a été envoyé par email.");
                return;
            }

            if (response.success) {
                toast.success("Connexion réussie !");
                dispatch(setAuth({ token: response.token, user: response.utilisateur }));
                localStorage.setItem("token", response.token);
                navigate("/EspaceClient");
            } else {
                toast.error(response.message || "Identifiants incorrects.");
            }
        } catch (err) {
            console.error(err);
            toast.error("Erreur lors de la connexion.");
        } finally {
            setLoading(false);
        }
    };

    // 🔐 Vérification du code → connexion
    const verifierCode = async (code) => {
        const res = await verifierCode2FA(deuxFacteurs.challengeId, code);
        if (res.success) {
            toast.success("Connexion réussie !");
            dispatch(setAuth({ token: res.token, user: res.utilisateur }));
            navigate("/EspaceClient");
            return res;
        }
        // Code expiré ou trop d'essais → retour au formulaire
        if (res.code === "expire" || res.code === "trop_essais") {
            toast.error(res.message);
            setDeuxFacteurs(null);
        }
        return res;
    };

    return (
        <>
            <Helmet>
                <title>Devenir partenaire — Thrundrz</title>
                <meta name="description" content="Devenez partenaire de Thrundrz et promouvez vos événements avec notre plateforme." />
                <link rel="canonical" href="https://thrundrz.fr/devenir-partenaire" />
            </Helmet>
            <Navbar />
            <main className="bg-color4 min-h-screen text-color2 lg:py-20 py-16 pt-24 px-1 lg:pt-36" id="login">
                <div className="w-full lg:px-20 px-3 mx-auto flex flex-col lg:flex-row gap-8 lg:gap-12">
                    {/* Bloc gauche */}
                    <div className="lg:w-2/5 w-full flex flex-col justify-center lg:gap-8 gap-4">
                        <h1 className="text-2xl md:text-3xl font-bold font-archivo leading-snug">
                            Devenez partenaire et boostez vos événements 🎉
                        </h1>
                        <ul className="flex flex-col lg:gap-5 gap-3">
                            {avantages.map(({ icone: Icone, texte }, index) => (
                                <li key={index} className="flex items-start gap-3">
                                    <div className="w-9 h-9 rounded-lg bg-white/10 flex items-center justify-center shrink-0 mt-0.5">
                                        <Icone size={18} className="text-white" aria-hidden="true" />
                                    </div>
                                    <span className="text-sm md:text-base leading-snug pt-1.5">{texte}</span>
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* Bloc droit */}
                    <div className="lg:w-3/5 w-full lg:p-8 p-5 rounded-2xl">

                        {/* Onglets Connexion / Inscription */}
                        <div className="grid grid-cols-2 gap-1 bg-black/20 rounded-xl p-1 mb-6 max-w-sm mx-auto">
                            <button
                                type="button"
                                onClick={() => setMode("login")}
                                className={`py-2.5 rounded-lg text-sm font-semibold transition ${mode === "login"
                                    ? "bg-color6 text-white shadow"
                                    : "text-white/60 hover:text-white"
                                    }`}
                            >
                                Connexion
                            </button>
                            <button
                                type="button"
                                onClick={() => { setMode("register"); setDeuxFacteurs(null); }}
                                className={`py-2.5 rounded-lg text-sm font-semibold transition ${mode === "register"
                                    ? "bg-color6 text-white shadow"
                                    : "text-white/60 hover:text-white"
                                    }`}
                            >
                                Inscription
                            </button>
                        </div>

                        {/* Formulaire d'inscription */}
                        {mode === "register" && (
                            <form onSubmit={handleRegister} className="flex flex-col gap-4" noValidate>
                                <div className="flex flex-col lg:flex-row gap-4">
                                    <Champ label="Nom complet" required error={erreursInscription.nomComplet}>
                                        <input
                                            type="text"
                                            autoComplete="name"
                                            placeholder="Votre nom complet"
                                            value={nomComplet}
                                            onChange={(e) => setNomComplet(e.target.value)}
                                            onBlur={(e) => validerChamp("nomComplet", e.target.value)}
                                            className={inputClasses(erreursInscription.nomComplet)}
                                        />
                                    </Champ>
                                    <Champ label="Email" required error={erreursInscription.email}>
                                        <input
                                            type="email"
                                            autoComplete="email"
                                            placeholder="Votre email"
                                            value={email}
                                            onChange={(e) => setEmail(e.target.value)}
                                            onBlur={(e) => validerChamp("email", e.target.value)}
                                            className={inputClasses(erreursInscription.email)}
                                        />
                                    </Champ>
                                </div>

                                <div className="flex flex-col lg:flex-row gap-4">
                                    <Champ label="Mot de passe" required error={erreursInscription.password}>
                                        <div className="relative">
                                            <input
                                                type={showPassword ? "text" : "password"}
                                                autoComplete="new-password"
                                                value={password}
                                                onChange={(e) => setPassword(e.target.value)}
                                                onBlur={(e) => validerChamp("password", e.target.value)}
                                                placeholder="6 caractères minimum"
                                                className={inputClasses(erreursInscription.password) + " pr-11"}
                                            />
                                            <button
                                                type="button"
                                                onClick={() => setShowPassword((v) => !v)}
                                                aria-label={showPassword ? "Masquer le mot de passe" : "Afficher le mot de passe"}
                                                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                                            >
                                                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                                            </button>
                                        </div>
                                    </Champ>
                                    <Champ label="Téléphone" required error={erreursInscription.telephone}>
                                        <input
                                            type="tel"
                                            autoComplete="tel"
                                            placeholder="Ex : 06 01 02 03 04"
                                            value={telephone}
                                            onChange={(e) => setTelephone(e.target.value)}
                                            onBlur={(e) => validerChamp("telephone", e.target.value)}
                                            className={inputClasses(erreursInscription.telephone)}
                                        />
                                    </Champ>
                                </div>

                                <div className="flex flex-col lg:flex-row gap-4">
                                    <Champ label="Raison sociale (optionnel)">
                                        <input
                                            type="text"
                                            autoComplete="organization"
                                            value={entreprise}
                                            onChange={(e) => setEntreprise(e.target.value)}
                                            placeholder="Nom de votre entreprise"
                                            className={inputClasses(false)}
                                        />
                                    </Champ>
                                    <Champ label="Numéro SIRET (optionnel)" error={erreursInscription.siret}>
                                        <input
                                            type="text"
                                            inputMode="numeric"
                                            value={siret}
                                            onChange={(e) => setSiret(e.target.value)}
                                            onBlur={(e) => validerChamp("siret", e.target.value)}
                                            placeholder="14 chiffres"
                                            className={inputClasses(erreursInscription.siret)}
                                        />
                                    </Champ>
                                </div>

                                <BoutonSubmit loading={loading}>
                                    {loading ? "Inscription en cours..." : "S'inscrire"}
                                </BoutonSubmit>

                                <div className="text-center flex lg:flex-row flex-col justify-center gap-1.5 lg:gap-3 text-sm text-white mt-2">
                                    <p>Vous avez déjà un compte ?</p>
                                    <button
                                        type="button"
                                        onClick={() => setMode("login")}
                                        className="underline text-color6 font-semibold"
                                    >
                                        Connectez-vous ici
                                    </button>
                                </div>
                            </form>
                        )}

                        {/* 🔐 Saisie du code reçu par mail */}
                        {mode === "login" && deuxFacteurs && (
                            <CodeVerification
                                email={deuxFacteurs.email}
                                onVerifier={verifierCode}
                                onRenvoyer={() => renvoyerCode2FA(deuxFacteurs.challengeId)}
                                onAnnuler={() => setDeuxFacteurs(null)}
                            />
                        )}

                        {/* Formulaire de connexion */}
                        {mode === "login" && !deuxFacteurs && (
                            <form onSubmit={handleLogin} className="flex flex-col gap-4 lg:px-16 px-2">
                                <Champ label="Email">
                                    <input
                                        type="email"
                                        autoComplete="email"
                                        value={loginEmail}
                                        onChange={(e) => setLoginEmail(e.target.value)}
                                        placeholder="Votre email"
                                        className={inputClasses(false)}
                                    />
                                </Champ>
                                <Champ label="Mot de passe">
                                    <div className="relative">
                                        <input
                                            type={showLoginPassword ? "text" : "password"}
                                            autoComplete="current-password"
                                            value={loginPassword}
                                            onChange={(e) => setLoginPassword(e.target.value)}
                                            placeholder="********"
                                            className={inputClasses(false) + " pr-11"}
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowLoginPassword((v) => !v)}
                                            aria-label={showLoginPassword ? "Masquer le mot de passe" : "Afficher le mot de passe"}
                                            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                                        >
                                            {showLoginPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                                        </button>
                                    </div>
                                </Champ>

                                <Link
                                    to="/mot-de-passe-oublie"
                                    className="text-xs text-white/60 hover:text-white underline self-end -mt-2"
                                >
                                    Mot de passe oublié ?
                                </Link>

                                <BoutonSubmit loading={loading}>
                                    {loading ? "Connexion en cours..." : "Se connecter"}
                                </BoutonSubmit>

                                <div className="text-center flex lg:flex-row flex-col justify-center gap-1.5 lg:gap-3 text-sm text-white mt-2">
                                    <p>Pas encore de compte ?</p>
                                    <button
                                        type="button"
                                        onClick={() => setMode("register")}
                                        className="underline text-color6 font-semibold"
                                    >
                                        Créez-en un ici
                                    </button>
                                </div>
                            </form>
                        )}
                    </div>
                </div>
                <ToastContainer />
            </main>
            <Footer />
        </>
    );
}